import React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonButton,
  IonIcon,
  IonTitle,
  IonContent,
  IonItem,
  IonLabel,
  IonList,
  IonFooter,
} from '@ionic/react'
import { chevronBack, informationCircle } from 'ionicons/icons'
import PCRHeader from '../../../components/pcrHeader/PCRHeader.component'
import SegmentedSelect, {
  ISegmentData,
  ISegmentOption,
  ISegmentPNNVOption,
} from '../../../components/segmentedSelect/SegmentedSelect.component'
import { useDispatch, useSelector } from 'react-redux'
import { getDiagnosisOptionsSelector } from '../../options/selectors/getDiagnosisOptions.selector'
import '../page/diagnosis.page.css'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { setOrganSystems, setOrganSystemPNNV } from '../store/diagnosis.actions'
import { DiagnosisConstants } from '../store/diagnosis.constants'
import { getDiagnosisSelector } from '../selectors/getDiagnosis.selector'
import { showAlert } from '../../../components/alert/store/alert.actions'

const System: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const dispatch = useDispatch()
  const diagnosisState = useSelector(getDiagnosisSelector)
  const { organSystems, organSystemsPNNV } = diagnosisState

  const diagnosisOptions = useSelector(getDiagnosisOptionsSelector)
  const { systemOptions } = diagnosisOptions

  const onBackClick = () => {
    save()
  }

  const pnnvSelectedInPrimaryTab =
    !!organSystemsPNNV && organSystemsPNNV.segmentId === +DiagnosisConstants.PRIMARY_SEGMENT_ID

  const segmentsMetdaData: ISegmentData[] = [
    {
      id: +DiagnosisConstants.PRIMARY_SEGMENT_ID,
      name: t('diagnosis.primary'),
      type: 'single',
      color: '#7145A9',
      disabled: false,
    },
    {
      id: +DiagnosisConstants.OTHER_SEGMENT_ID,
      name: t('diagnosis.other'),
      type: 'multi',
      color: '#599199',
      disabled: pnnvSelectedInPrimaryTab,
    },
  ]

  const onChange = (values: ISegmentOption[]) => {
    dispatch(setOrganSystems(values))
  }

  const onPNNVChange = (value: ISegmentPNNVOption | null) => {
    if (value?.segmentId === +DiagnosisConstants.PRIMARY_SEGMENT_ID && organSystems && organSystems.length > 0)
      dispatch(setOrganSystems([]))
    dispatch(setOrganSystemPNNV(value))
  }

  const isSaveValid = () => {
    const primaryValues = organSystems.filter((val) => val.segmentId === DiagnosisConstants.PRIMARY_SEGMENT_ID)
    const otherValues = organSystems.filter((val) => val.segmentId === DiagnosisConstants.OTHER_SEGMENT_ID)
    if (otherValues.length > 0 && primaryValues.length < 1) {
      dispatch(
        showAlert({
          message: t('diagnosis.primaryNotPresentError'),
        })
      )
      return false
    }
    return true
  }

  const clearSelectedValues = () => {
    dispatch(setOrganSystems([]))
    dispatch(setOrganSystemPNNV(null))
  }

  const save = () => {
    const isValid = isSaveValid()
    if (isValid) {
      history.push(TabConstants.CC_HPI)
    }
  }

  const infoPopupContent = () => {
    return (
      <IonList>
        <IonItem lines='none'>
          <IonIcon className='info-icon-margin' slot='start' icon={informationCircle}></IonIcon>
          <IonLabel>
            <h1>
              {t('diagnosis.organSystems')} {t('diagnosis.types')}
            </h1>
          </IonLabel>
        </IonItem>

        <IonItem lines='none'>
          <IonLabel>
            <h3>{t('diagnosis.primary')}</h3>
            <p style={{ whiteSpace: 'break-spaces' }}>{t('diagnosis.primarySystemDescr')}</p>
          </IonLabel>
        </IonItem>

        <hr />

        <IonItem lines='none'>
          <IonLabel>
            <h3>{t('diagnosis.other')}</h3>
            <p style={{ whiteSpace: 'break-spaces' }}>{t('diagnosis.otherSystemDescr')}</p>
          </IonLabel>
        </IonItem>
      </IonList>
    )
  }

  return (
    <IonPage>
      <PCRHeader />
      <IonHeader>
        <IonToolbar color='light'>
          <IonButtons slot='start'>
            <IonButton data-testid='Back' color='primary' onClick={onBackClick}>
              <IonIcon icon={chevronBack} />
              {t('common.buttons.Back')}
            </IonButton>
          </IonButtons>
          <IonTitle data-testid='HeaderTitle'>{t('diagnosis.organSystems')}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent color='light' fullscreen>
        <SegmentedSelect
          options={systemOptions}
          segmentsData={segmentsMetdaData}
          values={[...organSystems]}
          pnnvValue={organSystemsPNNV}
          onChange={onChange}
          onPNNVChange={onPNNVChange}
          infoPopupContent={infoPopupContent()}
        />
      </IonContent>
      <IonFooter className='ion-no-border'>
        <IonToolbar color='light' className='p-10'>
          <IonButtons>
            <IonButton
              data-testid='ClearAll'
              className='btn mr-8'
              color='danger'
              fill='outline'
              expand='block'
              onClick={() => clearSelectedValues()}>
              {t('common.buttons.ClearAll')}
            </IonButton>
            <IonButton data-testid='Save' className='btn btn-primary' expand='block' onClick={() => save()}>
              {t('common.buttons.Save')}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonFooter>
    </IonPage>
  )
}

export default System
